<template>
  <v-container>
    <v-responsive>
      <h1 class="ml-2 mb-5 mt-5">Adressänderung</h1>
      <div v-if="!orderSubmitted">
        <v-card elevation="1" outlined tile class="pa-2">
          <v-card-text>
            <v-form ref="form" v-model="isFormValid" @submit.prevent="sendForm">
              <!-- Gemeinde Selection -->
              <v-select
                v-model="formData.community"
                :items="communities"
                item-value="name"
                item-title="name"
                variant="outlined"
                label="Wählen Sie eine Gemeinde"
                density="comfortable"
                :rules="[(v) => !!v || 'Gemeinde ist erforderlich']"
                required
              ></v-select>

              <!-- Parcel Number -->
              <v-text-field
                v-model="formData.parcel"
                label="Grundstücksnummer"
                density="comfortable"
                :rules="[(v) => !!v || 'Grundstücksnummer ist erforderlich']"
                required
                variant="outlined"
              />

              <v-divider class="my-4"></v-divider>

              <!-- New Address Details -->
              <div class="mb-2">
                <h2>Angaben zur neuen Adresse</h2>
              </div>

              <v-text-field
                v-model="formData.first_name"
                label="Vorname"
                variant="outlined"
                density="comfortable"
                :rules="[(v) => !!v || 'Vorname ist erforderlich']"
                required
              />

              <v-text-field
                v-model="formData.last_name"
                variant="outlined"
                density="comfortable"
                label="Nachname"
                :rules="[(v) => !!v || 'Nachname ist erforderlich']"
                required
              />

              <!-- Street and House Number on the same line -->
              <v-row no-gutters>
                <v-col cols="12" md="8" class="pr-2">
                  <v-text-field
                    v-model="formData.street"
                    label="Strasse"
                    variant="outlined"
                    density="comfortable"
                    :rules="[(v) => !!v || 'Strasse ist erforderlich']"
                    required
                  />
                </v-col>
                <v-col cols="12" md="4" class="pl-2">
                  <v-text-field
                    v-model="formData.house_number"
                    label="Hausnummer"
                    variant="outlined"
                    density="comfortable"
                    :rules="[(v) => !!v || 'Hausnummer ist erforderlich']"
                    required
                  />
                </v-col>
              </v-row>

              <!-- Postal Code and City on the same line -->
              <v-row no-gutters>
                <v-col cols="12" md="4" class="pr-2">
                  <v-text-field
                    v-model="formData.postal_code"
                    variant="outlined"
                    label="PLZ"
                    required
                    density="comfortable"
                    :rules="[
                      (v) => !!v || 'PLZ ist erforderlich',
                      (v) =>
                        /^\d{4}$/.test(v) || 'PLZ muss 4 Ziffern enthalten',
                    ]"
                    @input="fetchCityByPostcode"
                  />
                </v-col>
                <v-col cols="12" md="8" class="pl-2">
                  <v-text-field
                    v-model="formData.city"
                    variant="outlined"
                    density="comfortable"
                    label="Ortschaft"
                    :rules="[(v) => !!v || 'Ortschaft ist erforderlich']"
                    required
                  />
                </v-col>
              </v-row>

              <v-textarea
                v-model="formData.remarks"
                variant="outlined"
                density="comfortable"
                label="Bemerkungen"
              />

              <v-divider class="my-4"></v-divider>

              <!-- Contact Information -->
              <div class="mb-2">
                <h2>Kontaktangaben für Rückfragen</h2>
              </div>

              <v-text-field
                v-model="formData.contact_info.first_name"
                variant="outlined"
                density="comfortable"
                label="Vorname"
                :rules="[(v) => !!v || 'Vorname ist erforderlich']"
                required
              />
              <v-text-field
                v-model="formData.contact_info.name"
                variant="outlined"
                density="comfortable"
                label="Nachname"
                :rules="[(v) => !!v || 'Nachname ist erforderlich']"
                required
              />

              <v-text-field
                v-model="formData.contact_info.email"
                variant="outlined"
                density="comfortable"
                label="Email"
                :rules="[
                  (v) => !!v || 'E-Mail ist erforderlich',
                  (v) => /.+@.+\..+/.test(v) || 'E-Mail muss gültig sein',
                ]"
                required
                type="email"
              />

              <v-text-field
                v-model="formData.contact_info.phone"
                variant="outlined"
                density="comfortable"
                label="Telefon"
                :rules="[(v) => !!v || 'Telefon ist erforderlich']"
                required
                type="tel"
              />
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Submit Button with Loading Spinner -->
        <div class="d-flex justify-end">
          <v-btn
            color="primary"
            type="submit"
            class="mt-10"
            @click="sendForm"
            :disabled="!isFormValid"
            :loading="isLoading"
          >
            Senden
          </v-btn>
        </div>
      </div>

      <!-- Success Message -->
      <v-card v-else elevation="1" outlined tile class="mt-10">
        <div class="text-center my-10">
          <v-icon size="40" color="success">mdi-check-circle</v-icon>
          <h2 class="my-5">Vielen Dank für Ihre Adressänderung!</h2>
          <p>
            Wir haben Ihre Anfrage erhalten und bearbeiten sie so schnell wie
            möglich.
          </p>
        </div>
      </v-card>
    </v-responsive>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AddressMutation',
  data() {
    return {
      communities: [],
      formData: {
        community: '',
        parcel: '',
        first_name: '',
        last_name: '',
        street: '',
        house_number: '',
        postal_code: '',
        city: '',
        remarks: '',
        contact_info: {
          name: '',
          first_name: '',
          email: '',
          phone: '',
        },
      },
      isFormValid: false,
      orderSubmitted: false,
      isLoading: false,
    }
  },
  mounted() {
    this.fetchCommunities()
  },
  watch: {
    formData: {
      handler: function () {
        this.validateForm()
      },
      deep: true,
    },
  },
  methods: {
    async fetchCommunities() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_ZKL_BACKEND_URL}/v1/services/municipality`,
        )
        this.communities = response.data
      } catch (error) {
        console.error('Fehler beim Abrufen der Gemeinden:', error)
      }
    },

    async fetchCityByPostcode() {
      if (!this.formData.postal_code || this.formData.postal_code.length < 4) {
        return
      }

      try {
        const response = await axios.get(
          'https://nominatim.openstreetmap.org/search',
          {
            params: {
              postalcode: this.formData.postal_code,
              country: 'Switzerland',
              format: 'json',
              'accept-language': 'de',
              addressdetails: 1,
              limit: 1,
            },
          },
        )
        if (response.data.length > 0) {
          // Extract only the city name from the response
          const address = response.data[0].address
          this.formData.city =
            address.city || address.town || address.village || ''
        } else {
          console.log('No data found for the provided postal code')
        }
      } catch (error) {
        console.error('Error fetching city by postcode:', error)
      }
    },

    validateForm() {
      this.isFormValid = this.$refs.form.validate()
    },

    async sendForm() {
      if (this.$refs.form.validate()) {
        this.isLoading = true
        try {
          const response = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/address-mutation`,
            this.formData,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          )
          console.log(response)

          this.orderSubmitted = true
          this.resetForm()
        } catch (error) {
          console.error('Error:', error.response ? error.response.data : error)
          alert(
            'Fehler beim Senden des Formulars. Bitte versuchen Sie es erneut.',
          )
        } finally {
          this.isLoading = false
        }
      }
    },

    resetForm() {
      this.formData = {
        community: '',
        parcel: '',
        first_name: '',
        last_name: '',
        street: '',
        house_number: '',
        postal_code: '',
        city: '',
        remarks: '',
        contact_info: {
          name: '',
          first_name: '',
          email: '',
          phone: '',
        },
      }
      this.$refs.form.reset()
      this.isFormValid = false
    },
  },
}
</script>

<style scoped>
/* Custom CSS to reduce margins if necessary */
.v-col {
  padding-top: 4px;
  padding-bottom: 4px;
}
</style>
